import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { STATUS_LABEL } from './CommsTabs';
import { CampaignWizard } from './CampaignWizard';

const dt = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export const CampaignList = () => {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get('/api/bulk/campaigns').then((r) => setRows(r.data)); }, []);
  return (
    <div className="p-4 space-y-3" data-testid="campaign-list">
      <div className="flex items-center"><h2 className="font-headings text-lg font-semibold">Bulk campaigns</h2>
        <Button size="sm" className="ml-auto" onClick={() => setOpen(true)} data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-1" />New campaign</Button></div>
      <div className="divide-y divide-border rounded-lg border border-border">
        {rows.map((c) => (
          <Link key={c.id} to={`/comms/bulk/${c.id}`} className="flex flex-wrap items-center gap-3 p-3 hover:bg-muted/40 transition-colors" data-testid={`campaign-row-${c.id}`}>
            <span className="font-medium">{c.name}</span><Badge variant="outline">{c.type === 'did_collection' ? 'Director ID' : 'Announcement'}</Badge><Badge>{c.status}</Badge>
            <span className="ml-auto text-xs text-muted-foreground">{c.metrics.unique_directors} directors · {c.metrics.completed} completed · {c.metrics.awaiting_review} to review · created {dt(c.created_at)}</span>
          </Link>))}
        {!rows.length && <p className="p-4 text-sm text-muted-foreground">No campaigns yet. Start one to collect Director IDs from every director in a few clicks.</p>}
      </div>
      <CampaignWizard open={open} onClose={() => setOpen(false)} onCreated={(id) => nav(`/comms/bulk/${id}`)} />
    </div>
  );
};

export const InfoRequests = () => {
  const [rows, setRows] = useState([]);
  const [st, setSt] = useState('');
  useEffect(() => { api.get('/api/bulk/requests', { params: { status: st || undefined } }).then((r) => setRows(r.data)); }, [st]);
  return (
    <div className="p-4 space-y-3" data-testid="info-requests">
      <div className="flex items-center gap-2"><h2 className="font-headings text-lg font-semibold">Information requests</h2>
        <select className="ml-auto h-9 rounded-md border border-input bg-background px-2 text-sm" value={st} onChange={(e) => setSt(e.target.value)} data-testid="info-requests-filter">
          <option value="">All</option>{['awaiting', 'awaiting_review', 'completed', 'pending_not_obtained', 'follow_up', 'cancelled'].map((k) => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}</select></div>
      <table className="w-full text-sm rounded-lg border border-border"><thead className="bg-muted/40 text-xs text-muted-foreground"><tr>{['Director', 'Email', 'Companies', 'Campaign', 'Delivery', 'Collection', 'Director ID'].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-t border-border" data-testid={`info-request-${r.id}`}>
            <td className="p-2 font-medium">{r.director}</td><td className="p-2 text-xs">{r.email || '—'}</td><td className="p-2">{r.companies}</td>
            <td className="p-2"><Link className="underline" to={`/comms/bulk/${r.campaign_id}`}>{r.campaign}</Link></td>
            <td className="p-2 text-xs">{STATUS_LABEL[r.delivery_status]}</td><td className="p-2 text-xs">{STATUS_LABEL[r.collection_status]}</td><td className="p-2 font-mono text-xs">{r.did_masked || '—'}</td>
          </tr>))}</tbody></table>
    </div>
  );
};

const KIND = { new: 'New Director ID', conflict: 'Conflict with ID on file', multiple: 'Several numbers – choose', sender_not_authorised: 'Sender not authorised', no_number: 'Reply without a Director ID' };

const Review = ({ r, onDone }) => {
  const [reason, setReason] = useState('');
  const decide = async (decision) => {
    try { await api.post(`/api/bulk/responses/${r.id}/decide`, { decision, reason: reason || undefined }); toast.success(decision === 'approve' ? 'Director ID recorded' : 'Rejected'); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="p-3 flex flex-wrap items-center gap-3" data-testid={`response-${r.id}`}>
      <Badge variant={r.kind === 'conflict' || r.kind === 'sender_not_authorised' ? 'destructive' : 'outline'}>{KIND[r.kind] || r.kind}</Badge>
      <span className="font-medium">{r.director}</span>
      <span className="font-mono text-xs">{r.proposed_masked || '—'}{r.current_masked ? ` (on file ${r.current_masked})` : ''}</span>
      <span className="text-xs text-muted-foreground">via {r.source.replace('_', ' ')}{r.note ? ` · ${r.note}` : ''}</span>
      <div className="ml-auto flex gap-2 items-center">
        <Input className="h-8 w-48" placeholder="Reason (to reject)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`response-reason-${r.id}`} />
        <Button size="sm" variant="outline" onClick={() => decide('reject')} disabled={!reason} data-testid={`response-reject-${r.id}`}>Reject</Button>
        {r.proposed_masked && <Button size="sm" onClick={() => decide('approve')} data-testid={`response-approve-${r.id}`}>Approve</Button>}
      </div>
    </div>
  );
};

export const ResponsesQueue = () => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/api/bulk/responses').then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 space-y-3" data-testid="responses-queue">
      <h2 className="font-headings text-lg font-semibold">Responses to review</h2>
      <p className="text-xs text-muted-foreground">Every update is approved by staff. Validation is “Format valid – check digit not verified”; a Director ID is not proof of identity.</p>
      <div className="divide-y divide-border rounded-lg border border-border">{rows.map((r) => <Review key={r.id} r={r} onDone={load} />)}
        {!rows.length && <p className="p-4 text-sm text-muted-foreground">Nothing waiting for review.</p>}</div>
    </div>
  );
};

export const TemplatesTab = () => {
  const [t, setT] = useState([]);
  const [vars, setVars] = useState([]);
  useEffect(() => { api.get('/api/bulk/templates').then((r) => { setT(r.data.templates); setVars(r.data.variables); }); }, []);
  const save = async (x) => { try { await api.put(`/api/bulk/templates/${x.id}`, { subject: x.subject, body: x.body }); toast.success('Template saved'); } catch (e) { toast.error(e.response?.data?.detail || 'Owners/admins only'); } };
  return (
    <div className="p-4 space-y-4" data-testid="templates-tab">
      <h2 className="font-headings text-lg font-semibold">Templates</h2>
      {t.map((x, i) => (
        <div key={x.id} className="rounded-lg border border-border p-3 space-y-2" data-testid={`template-${x.key}`}>
          <p className="font-medium">{x.name}</p>
          <Input value={x.subject} onChange={(e) => setT(t.map((y, j) => (j === i ? { ...y, subject: e.target.value } : y)))} data-testid={`template-subject-${x.key}`} />
          <Textarea rows={14} className="font-mono text-xs" value={x.body} onChange={(e) => setT(t.map((y, j) => (j === i ? { ...y, body: e.target.value } : y)))} data-testid={`template-body-${x.key}`} />
          <p className="text-xs text-muted-foreground">Variables: {vars.map((v) => `{{${v}}}`).join(' ')}</p>
          <Button size="sm" onClick={() => save(x)} data-testid={`template-save-${x.key}`}>Save</Button>
        </div>))}
    </div>
  );
};
